import { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';

export type AvailabilityType = 'VACATION' | 'SICK_LEAVE' | 'PERSONAL_LEAVE' | 'CUSTOM_HOURS' | 'BLOCKED';

export interface AvailabilityOverride {
    id: string;
    startDate: Date;
    endDate: Date;
    type: AvailabilityType;
    startTime?: string;
    endTime?: string;
    notes?: string;
}

export interface EmployeeAvailabilityManagerProps {
    employeeId: string;
    employeeName: string;
    employeePhotoUrl?: string;
    overrides: AvailabilityOverride[];
    onAddOverride: (override: Omit<AvailabilityOverride, 'id'>) => void;
    onDeleteOverride: (id: string) => void;
    onClose: () => void;
}

const EmployeeAvailabilityManager = ({
    employeeName,
    employeePhotoUrl,
    overrides,
    onAddOverride,
    onDeleteOverride,
    onClose
}: EmployeeAvailabilityManagerProps) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newOverride, setNewOverride] = useState<{
        type: AvailabilityType;
        startTime?: string;
        endTime?: string;
        notes?: string;
    }>({
        type: 'VACATION'
    });

    const typeConfig: Record<AvailabilityType, { label: string; color: string; icon: string; bgColor: string }> = {
        VACATION: {
            label: 'Vacaciones',
            color: 'text-blue-600',
            bgColor: 'bg-blue-100',
            icon: '🌴'
        },
        SICK_LEAVE: {
            label: 'Incapacidad',
            color: 'text-red-600',
            bgColor: 'bg-red-100',
            icon: '🏥'
        },
        PERSONAL_LEAVE: {
            label: 'Permiso Personal',
            color: 'text-purple-600',
            bgColor: 'bg-purple-100',
            icon: '🏠'
        },
        CUSTOM_HOURS: {
            label: 'Horario Especial',
            color: 'text-amber-600',
            bgColor: 'bg-amber-100',
            icon: '⏰'
        },
        BLOCKED: {
            label: 'Bloqueado',
            color: 'text-gray-600',
            bgColor: 'bg-gray-200',
            icon: '🚫'
        }
    };

    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

    // Calculate the start day of the week (0 = Sunday)
    const startDay = monthStart.getDay();
    const emptyDays = Array(startDay).fill(null);

    const getOverrideForDay = (day: Date): AvailabilityOverride | undefined => {
        return overrides.find(o => {
            // Parse dates and normalize to start of day to avoid timezone issues
            const start = new Date(o.startDate);
            const end = new Date(o.endDate);

            // Compare only year, month, day (ignore time component)
            const dayNorm = new Date(day.getFullYear(), day.getMonth(), day.getDate());
            const startNorm = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            const endNorm = new Date(end.getFullYear(), end.getMonth(), end.getDate());

            return dayNorm >= startNorm && dayNorm <= endNorm;
        });
    };

    const isDateSelected = (day: Date) => selectedDates.some(d => isSameDay(d, day));

    const handleDayClick = (day: Date) => {
        if (isDateSelected(day)) {
            setSelectedDates(selectedDates.filter(d => !isSameDay(d, day)));
        } else {
            setSelectedDates([...selectedDates, day].sort((a, b) => a.getTime() - b.getTime()));
        }
    };

    const handleSubmitOverride = () => {
        if (selectedDates.length === 0) return;

        const startDate = selectedDates[0];
        const endDate = selectedDates[selectedDates.length - 1];

        onAddOverride({
            startDate,
            endDate,
            type: newOverride.type,
            startTime: newOverride.startTime,
            endTime: newOverride.endTime,
            notes: newOverride.notes
        });

        setSelectedDates([]);
        setShowAddModal(false);
        setNewOverride({ type: 'VACATION' });
    };

    const upcomingOverrides = useMemo(() => {
        const today = new Date();
        return overrides
            .filter(o => new Date(o.endDate) >= today)
            .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
            .slice(0, 5);
    }, [overrides]);

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
                {/* Header */}
                <div className="px-6 py-5 bg-gradient-to-r from-primary to-primary/80 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {employeePhotoUrl ? (
                                <img src={employeePhotoUrl} alt={employeeName} className="w-14 h-14 rounded-2xl object-cover ring-4 ring-white/30" />
                            ) : (
                                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-2xl font-bold">
                                    {employeeName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                </div>
                            )}
                            <div>
                                <h2 className="text-xl font-bold">{employeeName}</h2>
                                <p className="text-white/80 text-sm">Gestión de Disponibilidad</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto p-6">
                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Calendar Section */}
                        <div className="md:col-span-2 space-y-4">
                            {/* Month Navigation */}
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <h3 className="text-lg font-bold text-gray-900 capitalize">
                                    {format(currentMonth, 'MMMM yyyy', { locale: es })}
                                </h3>
                                <button
                                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>

                            {/* Calendar Grid */}
                            <div className="bg-gray-50 rounded-2xl p-4">
                                {/* Day Headers */}
                                <div className="grid grid-cols-7 gap-1 mb-2">
                                    {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                                        <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
                                            {day}
                                        </div>
                                    ))}
                                </div>

                                {/* Days Grid */}
                                <div className="grid grid-cols-7 gap-1">
                                    {/* Empty cells for alignment */}
                                    {emptyDays.map((_, i) => (
                                        <div key={`empty-${i}`} className="aspect-square" />
                                    ))}

                                    {/* Actual days */}
                                    {daysInMonth.map(day => {
                                        const override = getOverrideForDay(day);
                                        const selected = isDateSelected(day);
                                        const today = isToday(day);

                                        return (
                                            <button
                                                key={day.toISOString()}
                                                onClick={() => handleDayClick(day)}
                                                className={`
                                                    aspect-square rounded-xl flex flex-col items-center justify-center text-sm font-medium
                                                    transition-all duration-200 relative group
                                                    ${!isSameMonth(day, currentMonth) ? 'text-gray-300' : ''}
                                                    ${today ? 'ring-2 ring-primary ring-offset-2' : ''}
                                                    ${selected ? 'bg-primary text-white shadow-lg scale-105' : ''}
                                                    ${override && !selected ? typeConfig[override.type].bgColor : ''}
                                                    ${!override && !selected ? 'hover:bg-gray-200' : ''}
                                                `}
                                            >
                                                <span>{format(day, 'd')}</span>
                                                {override && !selected && (
                                                    <span className="text-xs">{typeConfig[override.type].icon}</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Selected Dates Action Bar */}
                            {selectedDates.length > 0 && (
                                <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4 flex items-center justify-between animate-in slide-in-from-bottom-2 duration-200">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <span className="text-primary font-bold">{selectedDates.length}</span>
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">
                                                {selectedDates.length === 1
                                                    ? format(selectedDates[0], 'd MMM yyyy', { locale: es })
                                                    : `${format(selectedDates[0], 'd MMM', { locale: es })} - ${format(selectedDates[selectedDates.length - 1], 'd MMM yyyy', { locale: es })}`
                                                }
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {selectedDates.length} día{selectedDates.length !== 1 ? 's' : ''} seleccionado{selectedDates.length !== 1 ? 's' : ''}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setSelectedDates([])}
                                            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={() => setShowAddModal(true)}
                                            className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                            Marcar Ausencia
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar - Upcoming Absences */}
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold text-gray-900">Próximas Ausencias</h4>

                            {upcomingOverrides.length === 0 ? (
                                <div className="text-center py-8 bg-gray-50 rounded-2xl">
                                    <div className="text-4xl mb-2">✨</div>
                                    <p className="text-sm text-gray-500">No hay ausencias programadas</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {upcomingOverrides.map(override => (
                                        <div
                                            key={override.id}
                                            className={`p-4 rounded-xl ${typeConfig[override.type].bgColor} relative group`}
                                        >
                                            <button
                                                onClick={() => onDeleteOverride(override.id)}
                                                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/80 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity hover:bg-red-100"
                                            >
                                                <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                            <div className="flex items-start gap-3">
                                                <span className="text-2xl">{typeConfig[override.type].icon}</span>
                                                <div>
                                                    <p className={`text-sm font-semibold ${typeConfig[override.type].color}`}>
                                                        {typeConfig[override.type].label}
                                                    </p>
                                                    <p className="text-xs text-gray-600 mt-0.5">
                                                        {format(new Date(override.startDate), 'd MMM', { locale: es })}
                                                        {!isSameDay(new Date(override.startDate), new Date(override.endDate)) && (
                                                            <> - {format(new Date(override.endDate), 'd MMM', { locale: es })}</>
                                                        )}
                                                    </p>
                                                    {override.startTime && override.endTime && (
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {override.startTime} - {override.endTime}
                                                        </p>
                                                    )}
                                                    {override.notes && (
                                                        <p className="text-xs text-gray-500 mt-1 italic">"{override.notes}"</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Legend */}
                            <div className="pt-4 border-t border-gray-200">
                                <h5 className="text-xs font-semibold text-gray-500 uppercase mb-3">Leyenda</h5>
                                <div className="space-y-2">
                                    {Object.entries(typeConfig).map(([key, config]) => (
                                        <div key={key} className="flex items-center gap-2 text-xs">
                                            <span>{config.icon}</span>
                                            <span className={config.color}>{config.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Override Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-5 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-900">Registrar Ausencia</h3>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Ausencia</label>
                            <div className="grid grid-cols-2 gap-2">
                                {Object.entries(typeConfig).map(([key, config]) => (
                                    <button
                                        key={key}
                                        onClick={() => setNewOverride(prev => ({ ...prev, type: key as AvailabilityType }))}
                                        className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 ${newOverride.type === key
                                            ? 'border-primary bg-primary/5'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <span className="text-xl">{config.icon}</span>
                                        <span className="text-sm font-medium">{config.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {newOverride.type === 'CUSTOM_HOURS' && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora Inicio</label>
                                    <input
                                        type="time"
                                        value={newOverride.startTime || ''}
                                        onChange={(e) => setNewOverride(prev => ({ ...prev, startTime: e.target.value }))}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora Fin</label>
                                    <input
                                        type="time"
                                        value={newOverride.endTime || ''}
                                        onChange={(e) => setNewOverride(prev => ({ ...prev, endTime: e.target.value }))}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Notas (Opcional)</label>
                            <textarea
                                value={newOverride.notes || ''}
                                onChange={(e) => setNewOverride(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Razón o comentarios..."
                                rows={2}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                            />
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSubmitOverride}
                                className="flex-1 px-4 py-2.5 bg-primary text-white font-medium rounded-xl hover:bg-primary/90 transition-colors"
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeAvailabilityManager;
